import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi, formatApiError } from "@/lib/api";
import { Trash2, Eye, UserPlus, Search } from "lucide-react";
import { isProClient } from "./ClientInfoSection";

const norm = (v) => (v || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const digits = (v) => (v || "").toString().replace(/\D/g, "");

export default function AdminClients() {
  const [users, setUsers] = useState([]);
  const [err, setErr] = useState("");
  const [creating, setCreating] = useState(false);
  const emptyForm = {
    client_type: "particulier",
    prenom: "", nom: "", email: "", phone: "", password: "",
    adresse: "", code_postal: "", ville: "",
    // Pro / Asso fields
    raison_sociale: "", nom_commercial: "", siren: "", siret: "",
    tva_number: "", tva_status: "",
    contact_prenom: "", contact_nom: "", contact_fonction: "",
  };
  const [form, setForm] = useState(emptyForm);
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const filtered = useMemo(() => {
    const q = norm(query.trim());
    const qd = digits(query);
    return users.filter((u) => {
      if (typeFilter === "pro" && !isProClient(u)) return false;
      if (typeFilter === "particulier" && isProClient(u)) return false;
      if (!q) return true;
      const hay = norm([u.name, u.prenom, u.nom, u.email, u.raison_sociale, u.nom_commercial, u.contact_nom, u.contact_prenom, u.client_number].join(" "));
      return hay.includes(q) || (qd.length >= 3 && digits(u.phone).includes(qd));
    });
  }, [users, query, typeFilter]);
  const isPro = form.client_type === "professionnel" || form.client_type === "association";

  const load = () => adminApi.users().then(setUsers).catch((e) => setErr(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const remove = async (id, email) => {
    if (!window.confirm(`Supprimer ${email} et toutes ses données ?`)) return;
    try { await adminApi.deleteUser(id); load(); } catch (e) { alert(formatApiError(e)); }
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      // Strip empty strings for optional fields so backend validators don't reject empty SIRET/SIREN
      const payload = { ...form, role: "client" };
      ["siren", "siret", "tva_number", "tva_status", "rna"].forEach((k) => {
        if (!payload[k]) delete payload[k];
      });
      if (form.client_type === "particulier") {
        // Don't send pro-specific fields if particulier selected
        ["raison_sociale", "nom_commercial", "siren", "siret", "rna",
         "tva_number", "tva_status", "contact_prenom", "contact_nom", "contact_fonction"
        ].forEach((k) => { delete payload[k]; });
      }
      await adminApi.createUser(payload);
      setForm(emptyForm);
      setCreating(false);
      load();
    } catch (e2) { alert(formatApiError(e2)); }
  };

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const INPUT_CLS = "bg-black/60 border border-[#DCB457]/25 px-3 py-2.5 text-sm focus:border-[#DCB457] outline-none";

  return (
    <div data-testid="admin-clients">
      <header className="mb-8 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow">Administration</p>
          <h1 className="font-serif-luxe text-4xl md:text-5xl mt-3">Liste des <span className="italic gold-text">clients.</span></h1>
        </div>
        <button onClick={() => setCreating((c) => !c)} data-testid="admin-toggle-create" className="btn-ghost-gold inline-flex items-center gap-2">
          <UserPlus size={14} strokeWidth={1.4} /> {creating ? "Annuler" : "Créer un client"}
        </button>
      </header>

      {creating && (
        <form onSubmit={create} className="card-luxe p-6 mb-6" data-testid="admin-create-form">
          {/* Type de client — toggle Pro / Particulier / Association */}
          <div className="mb-5 flex items-center gap-2 flex-wrap" data-testid="ac-client-type-group">
            <span className="text-[10px] uppercase tracking-[0.2em] text-white/55 mr-2">Type de compte</span>
            {[
              { v: "particulier", label: "Particulier (B2C)" },
              { v: "professionnel", label: "Professionnel (B2B)" },
              { v: "association", label: "Association / Organisme" },
            ].map((opt) => (
              <button
                key={opt.v}
                type="button"
                onClick={() => upd("client_type", opt.v)}
                data-testid={`ac-type-${opt.v}`}
                className={`px-3 py-1.5 text-xs border transition ${
                  form.client_type === opt.v
                    ? "bg-[#DCB457] text-black border-[#DCB457]"
                    : "border-[#DCB457]/30 text-white/70 hover:border-[#DCB457]/60"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Common fields */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <input required placeholder="Prénom" value={form.prenom} onChange={(e) => upd("prenom", e.target.value)} className={INPUT_CLS} data-testid="ac-prenom" />
            <input required placeholder="Nom" value={form.nom} onChange={(e) => upd("nom", e.target.value)} className={INPUT_CLS} data-testid="ac-nom" />
            <input required type="email" placeholder="Email" value={form.email} onChange={(e) => upd("email", e.target.value)} className={INPUT_CLS} data-testid="ac-email" />
            <input placeholder="Téléphone" value={form.phone} onChange={(e) => upd("phone", e.target.value)} className={INPUT_CLS} data-testid="ac-phone" />
            <input required type="text" placeholder="Mot de passe initial (6+)" value={form.password} onChange={(e) => upd("password", e.target.value)} className={INPUT_CLS} data-testid="ac-password" />
          </div>

          {/* Pro / Association block — only shown when relevant */}
          {isPro && (
            <div className="mt-5 pt-5 border-t border-[#DCB457]/15" data-testid="ac-pro-block">
              <p className="eyebrow mb-3">
                {form.client_type === "association" ? "Informations association" : "Informations entreprise"}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input placeholder={form.client_type === "association" ? "Nom de l'association" : "Raison sociale"} value={form.raison_sociale} onChange={(e) => upd("raison_sociale", e.target.value)} className={INPUT_CLS} data-testid="ac-raison-sociale" />
                <input placeholder="Nom commercial (optionnel)" value={form.nom_commercial} onChange={(e) => upd("nom_commercial", e.target.value)} className={INPUT_CLS} data-testid="ac-nom-commercial" />
                <input placeholder="SIREN (9 chiffres)" value={form.siren} onChange={(e) => upd("siren", e.target.value)} className={INPUT_CLS} data-testid="ac-siren" maxLength={14} />
                <input placeholder="SIRET (14 chiffres)" value={form.siret} onChange={(e) => upd("siret", e.target.value)} className={INPUT_CLS} data-testid="ac-siret" maxLength={17} />
                <input placeholder="N° TVA intracom (FR...)" value={form.tva_number} onChange={(e) => upd("tva_number", e.target.value)} className={INPUT_CLS} data-testid="ac-tva" />
                <select value={form.tva_status} onChange={(e) => upd("tva_status", e.target.value)} className={INPUT_CLS} data-testid="ac-tva-status">
                  <option value="">Statut TVA…</option>
                  <option value="assujetti">Assujetti TVA</option>
                  <option value="franchise_base">Franchise en base</option>
                  <option value="non_assujetti">Non assujetti</option>
                  <option value="exonere">Exonéré</option>
                  <option value="autre">Autre</option>
                </select>
              </div>
              <p className="eyebrow mt-5 mb-3">Contact signataire</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input placeholder="Prénom contact" value={form.contact_prenom} onChange={(e) => upd("contact_prenom", e.target.value)} className={INPUT_CLS} data-testid="ac-contact-prenom" />
                <input placeholder="Nom contact" value={form.contact_nom} onChange={(e) => upd("contact_nom", e.target.value)} className={INPUT_CLS} data-testid="ac-contact-nom" />
                <input placeholder="Fonction (Dir., DAF…)" value={form.contact_fonction} onChange={(e) => upd("contact_fonction", e.target.value)} className={INPUT_CLS} data-testid="ac-contact-fonction" />
              </div>
            </div>
          )}

          {/* Address block (both Pro and Particulier) */}
          <div className="mt-5 pt-5 border-t border-[#DCB457]/15">
            <p className="eyebrow mb-3">Adresse de facturation</p>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
              <input placeholder="Adresse" value={form.adresse} onChange={(e) => upd("adresse", e.target.value)} className={`${INPUT_CLS} md:col-span-3`} data-testid="ac-adresse" />
              <input placeholder="CP" value={form.code_postal} onChange={(e) => upd("code_postal", e.target.value)} className={INPUT_CLS} data-testid="ac-cp" />
              <input placeholder="Ville" value={form.ville} onChange={(e) => upd("ville", e.target.value)} className={`${INPUT_CLS} md:col-span-2`} data-testid="ac-ville" />
            </div>
          </div>

          <button type="submit" className="btn-gold w-full mt-6" data-testid="ac-submit">
            {isPro ? "Créer le compte professionnel" : "Créer le compte particulier"}
          </button>
        </form>
      )}

      {err && <p className="text-sm text-red-300 mb-4">{err}</p>}

      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4" data-testid="admin-clients-toolbar">
        <div className="relative flex-1">
          <Search size={14} strokeWidth={1.4} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher par nom, téléphone, société ou email…" className="w-full pl-9 pr-3 py-2.5 bg-black/50 border border-[#DCB457]/25 text-sm focus:border-[#DCB457] outline-none" data-testid="admin-clients-search" />
        </div>
        <div className="flex gap-2">
          {[["all", "Tous"], ["pro", "Pro"], ["particulier", "Particuliers"]].map(([v, l]) => (
            <button key={v} type="button" onClick={() => setTypeFilter(v)} data-testid={`admin-clients-filter-${v}`}
              className={`text-[11px] uppercase tracking-[0.18em] px-3 py-2 border transition-colors ${typeFilter === v ? "border-[#DCB457] text-[#DCB457] bg-[#DCB457]/10" : "border-white/12 text-white/55 hover:border-[#DCB457]/40"}`}>{l}</button>
          ))}
        </div>
      </div>
      <p className="text-[11px] text-white/45 mb-2" data-testid="admin-clients-count">{filtered.length} client{filtered.length > 1 ? "s" : ""}</p>

      <div className="card-luxe overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-users-table">
          <thead className="bg-black/40 text-[10px] uppercase tracking-[0.2em] text-white/55">
            <tr>
              <th className="px-5 py-4 text-left">Nom</th>
              <th className="px-5 py-4 text-left">Type</th>
              <th className="px-5 py-4 text-left">Email</th>
              <th className="px-5 py-4 text-left hidden md:table-cell">Téléphone</th>
              <th className="px-5 py-4 text-left hidden md:table-cell">Rôle</th>
              <th className="px-5 py-4 text-left hidden lg:table-cell">Créé le</th>
              <th className="px-5 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && (
              <tr><td colSpan={7} className="px-5 py-12 text-center text-white/40 italic">Aucun client pour l'instant.</td></tr>
            )}
            {users.length > 0 && filtered.length === 0 && (
              <tr><td colSpan={7} className="px-5 py-12 text-center text-white/40 italic" data-testid="admin-clients-no-result">Aucun client ne correspond à la recherche.</td></tr>
            )}
            {filtered.map((u) => (
              <tr key={u.id} className="border-t border-[#DCB457]/10 hover:bg-[#DCB457]/5" data-testid={`row-${u.id}`}>
                <td className="px-5 py-4">
                  {u.name}
                  {u.raison_sociale && <span className="block text-xs text-[#DCB457]/80 mt-0.5">{u.raison_sociale}</span>}
                </td>
                <td className="px-5 py-4">
                  {u.role !== "admin" && (
                    <span data-testid={`type-${u.id}`} className={`text-[10px] uppercase tracking-[0.18em] border px-2 py-1 whitespace-nowrap ${isProClient(u) ? "text-[#DCB457] border-[#DCB457]/50" : "text-white/55 border-white/20"}`}>
                      {isProClient(u) ? (u.client_type === "association" ? "Asso" : "Pro") : "Particulier"}
                    </span>
                  )}
                </td>
                <td className="px-5 py-4 text-white/65">{u.email}</td>
                <td className="px-5 py-4 text-white/55 hidden md:table-cell">{u.phone || "—"}</td>
                <td className="px-5 py-4 hidden md:table-cell">
                  <span className={`text-[10px] uppercase tracking-[0.18em] border px-2 py-1 ${u.role === "admin" ? "text-[#DCB457] border-[#DCB457]/50" : "text-white/55 border-white/20"}`}>
                    {u.role}
                  </span>
                </td>
                <td className="px-5 py-4 text-white/45 hidden lg:table-cell text-xs">{u.created_at?.slice(0, 10)}</td>
                <td className="px-5 py-4 text-right">
                  <div className="inline-flex items-center gap-2">
                    <Link to={`/admin/clients/${u.id}`} data-testid={`view-${u.id}`} className="inline-flex items-center gap-1 text-[#DCB457] border border-[#DCB457]/40 px-2.5 py-1.5 hover:bg-[#DCB457]/10 transition text-xs">
                      <Eye size={12} strokeWidth={1.4} /> Voir
                    </Link>
                    {u.role !== "admin" && (
                      <button onClick={() => remove(u.id, u.email)} data-testid={`del-${u.id}`} className="inline-flex items-center gap-1 text-red-300 border border-red-400/30 px-2.5 py-1.5 hover:bg-red-500/10 transition text-xs">
                        <Trash2 size={12} strokeWidth={1.4} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
